// src/components/calendar/CalendarCell.js
import React, { useContext, useEffect, useRef, useState } from "react";
import { TableCell, Tooltip, CircularProgress, Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MoreTimeIcon from "@mui/icons-material/MoreTime";
import CellMenu from "./CellMenu";
import licenseColors from "../../config/licenseColors";
import { CalendarContext } from "../context/CalendarContext";
import { OVERTIME_TYPE_LABELS } from "./edit/Overtime";

const parseModificationDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  let normalizedValue =
    typeof value === "string" && value.includes(" ") && !value.includes("T")
      ? value.replace(" ", "T")
      : value;

  if (
    typeof normalizedValue === "string" &&
    !/(Z|[+-]\d{2}:\d{2})$/.test(normalizedValue)
  ) {
    normalizedValue = `${normalizedValue}Z`;
  }

  const parsed = new Date(normalizedValue);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatModificationDate = (value) => {
  const parsed = parseModificationDate(value);
  if (!parsed) return value || "";

  return new Intl.DateTimeFormat("es-UY", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "America/Montevideo",
  }).format(parsed);
};

// El backend no siempre manda estas fechas en el mismo formato (a veces ISO,
// a veces HTTP-date), asi que se parsean de forma tolerante, igual que en
// LicenseTable.js.
const formatShortDate = (value) => {
  if (!value) return "";

  const isoMatch = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return `${day}/${month}/${year}`;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const year = date.getUTCFullYear();
  return `${day}/${month}/${year}`;
};

const CalendarCell = ({
  day,
  employeeData,
  station_id,
  workarea_id,
  workshifts,
  isWeekend = false,
  isHoliday = false,
  readOnly = false,
  onBusyChange,
  busyState = null,
}) => {
  const theme = useTheme();
  const { getOvertimeForDay } = useContext(CalendarContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const [lockedTooltipOpen, setLockedTooltipOpen] = useState(false);
  const lockedTooltipTimerRef = useRef(null);

  const {
    ci: employeeCi = "",
    description_license = "",
    workshift = null,               // puede venir string o objeto { id_workshift, name_workshift, ... }
    work_public_holiday = false,
    free_day = false,
    name_type_license = "",
    license_date_from = null,
    license_date_to = null,
    workshift_manager = false,
    workshift_id: wsIdFromProp,     // puede venir aparte
    otherRoleShiftName = null,      // ✅ NUEVO
    free_day_reason = null,
    is_public_holiday = false,
    first_day = null,
    employment_locked = false,
    modified = [],
  } = employeeData || {};

  const isEmploymentLocked = Boolean(employment_locked);
  const lockedTooltip = first_day
    ? `El empleado aún no ingresó en esta fecha. Podrá editarse desde ${first_day}.`
    : "El empleado aún no ingresó en esta fecha.";

  useEffect(() => () => {
    if (lockedTooltipTimerRef.current) {
      clearTimeout(lockedTooltipTimerRef.current);
    }
  }, []);

  // Normalizaciones robustas
  const wsObj = typeof workshift === "object" && workshift !== null ? workshift : null;
  const wsId = wsObj?.id_workshift ?? (wsIdFromProp != null ? Number(wsIdFromProp) : undefined);
  const wsName =
    typeof workshift === "string"
      ? workshift
      : wsObj?.name_workshift || "Sin turno";

  const handleMenuOpen = (e) => {
    if (readOnly || isEmploymentLocked) return;
    setAnchorEl(e.currentTarget);
  };

  const showLockedTooltip = () => {
    if (lockedTooltipTimerRef.current) {
      clearTimeout(lockedTooltipTimerRef.current);
    }

    setLockedTooltipOpen(true);
    lockedTooltipTimerRef.current = setTimeout(() => {
      setLockedTooltipOpen(false);
      lockedTooltipTimerRef.current = null;
    }, 2200);
  };

  const handleCellClick = (e) => {
    if (isEmploymentLocked) {
      e.preventDefault();
      e.stopPropagation();
      showLockedTooltip();
      return;
    }

    handleMenuOpen(e);
  };

  const handleMenuClose = () => setAnchorEl(null);

  // Si pasa a readOnly con el menú abierto, lo cerramos
  if ((readOnly || isEmploymentLocked) && anchorEl) {
    queueMicrotask(() => setAnchorEl(null));
  }

  // Está en S/A en este rol
  const isSA =
    isEmploymentLocked || (
      !description_license &&
      !work_public_holiday &&
      !free_day &&
      (wsId === 1 || /^(S\/A|Sin turno|Sin asignar)$/i.test(String(wsName || "")))
    );

  // ✅ Si en este rol está S/A pero en otro rol sí tiene turno, mostrarlo entre paréntesis
  const showOtherRoleShift = isSA && Boolean(otherRoleShiftName);
  const holidayTooltip = is_public_holiday
    ? work_public_holiday
      ? "Feriado oficial: no trabaja feriado."
      : free_day
        ? "Feriado oficial: no trabaja por día libre."
        : description_license
          ? "Feriado oficial: no trabaja por inasistencia."
          : "Feriado oficial: trabaja feriado."
    : "";

  const displayText = isEmploymentLocked
    ? "S/A"
    : free_day
      ? "LIB"
      : description_license
        ? description_license
        : work_public_holiday
          ? "FER"
          : showOtherRoleShift
            ? `(${otherRoleShiftName})`
            : wsName || "Sin asignar";

  const hasBreak = Boolean(wsObj?.break_from && wsObj?.break_to);
  const breakTooltipText = hasBreak
    ? `Descanso: ${String(wsObj.break_from).slice(0, 5)}–${String(wsObj.break_to).slice(0, 5)}`
    : "";

  const hasLicenseInfo = Boolean(description_license);
  const licenseTypeLabel = name_type_license || description_license;
  const licenseRangeText =
    license_date_from && license_date_to
      ? `${formatShortDate(license_date_from)} – ${formatShortDate(license_date_to)}`
      : license_date_from
        ? `Desde ${formatShortDate(license_date_from)}`
        : "";

  const hasAutoFreeDayRule = free_day && Boolean(free_day_reason);
  const latestManualModification =
    Array.isArray(modified) && modified.length > 0 ? modified[modified.length - 1] : null;
  const hasManualModification = Boolean(latestManualModification);
  const manualModificationTitle = latestManualModification?.reason || "Modificación manual";
  const manualModificationActor = latestManualModification?.actor_email || null;
  const manualModificationDate = formatModificationDate(latestManualModification?.date);
  const overtimeItems = getOvertimeForDay ? getOvertimeForDay(employeeCi, day) : [];
  const hasOvertime = overtimeItems.length > 0;
  const overtimeTooltipTitle = hasOvertime ? (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      <Box component="span" sx={{ fontWeight: 700 }}>Horas extra</Box>
      {overtimeItems.map((item) => (
        <Box component="span" key={item.id}>
          {(OVERTIME_TYPE_LABELS[item.type] || item.type)}: {String(item.hour_from).slice(0, 5)}–{String(item.hour_to).slice(0, 5)}
        </Box>
      ))}
    </Box>
  ) : "";

  const isBusyTarget = Boolean(
    busyState?.employeeCi === String(employeeData?.ci) &&
      (
        busyState?.scope === "period" ||
        busyState?.day === day
      )
  );
  const showCellSpinner = isBusyTarget && busyState?.scope !== "period";

  const getBackgroundColor = () => {
    if (free_day) return "#C8E6C9";                   // Libre
    if (description_license) return "#50d2fe";        // Licencia
    if (work_public_holiday) return "#FFCDD2";        // Feriado no trabajado
    if (isHoliday) return "#FFF5F5";                  // Columna de feriado
    if (isSA) return theme.palette.grey[100];         // S/A suave
    if (isWeekend) return "#FFFCF2";                  // Fin de semana casi imperceptible
    return (name_type_license && licenseColors[name_type_license]?.color) || "";
  };

  const textColor =
    workshift_manager && !free_day && !work_public_holiday && !description_license
      ? theme.palette.secondary.light
      : isSA
      ? theme.palette.grey[600]
      : "#000";

  const fontWeight =
    workshift_manager && !free_day && !work_public_holiday && !description_license
      ? "bold"
      : "normal";
  const baseTooltipText = isEmploymentLocked
    ? lockedTooltip
    : hasAutoFreeDayRule
      ? free_day_reason
      : holidayTooltip;
  const tooltipTitle = hasManualModification || baseTooltipText || hasBreak || hasLicenseInfo ? (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      {hasLicenseInfo ? (
        <Box component="span" sx={{ display: "flex", flexDirection: "column" }}>
          <Box component="span" sx={{ fontWeight: 700 }}>{licenseTypeLabel}</Box>
          {licenseRangeText ? <Box component="span">{licenseRangeText}</Box> : null}
        </Box>
      ) : null}
      {baseTooltipText ? <Box component="span">{baseTooltipText}</Box> : null}
      {hasBreak ? <Box component="span">{breakTooltipText}</Box> : null}
      {hasManualModification ? (
        <Box
          component="span"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.25,
            borderTop: baseTooltipText ? "1px solid rgba(255,255,255,0.24)" : "none",
            pt: baseTooltipText ? 0.5 : 0,
          }}
        >
          <Box component="span" sx={{ fontWeight: 700 }}>
            Ultima modificacion manual
          </Box>
          <Box component="span">{manualModificationTitle}</Box>
          {manualModificationActor ? (
            <Box component="span">Por: {manualModificationActor}</Box>
          ) : null}
          {manualModificationDate ? (
            <Box component="span">{manualModificationDate}</Box>
          ) : null}
        </Box>
      ) : null}
    </Box>
  ) : "";

  return (
    <>
      <TableCell
        align="center"
        onClick={handleCellClick}
        aria-disabled={readOnly || isEmploymentLocked}
        sx={{
          backgroundColor: getBackgroundColor(),
          borderLeft: isHoliday
            ? "1px solid #F0C9C9"
            : isWeekend
              ? "1px solid #EAD9A5"
              : "1px solid #e0e0e0",
          borderRight: isHoliday
            ? "1px solid #F6DDDD"
            : isWeekend
              ? "1px solid #F3E8C4"
              : undefined,
          borderBottom: isHoliday
            ? "1px solid #F0C9C9"
            : isWeekend
              ? "1px solid #EAD9A5"
              : "1px solid #e0e0e0",
          cursor: readOnly ? "default" : isEmploymentLocked ? "not-allowed" : "pointer",
          position: "relative",
          p: "3px",
          minWidth: "30px",
          maxWidth: "40px",
          fontSize: "0.9rem",
          outline: showCellSpinner ? "2px solid rgba(0, 232, 117, 0.85)" : undefined,
          outlineOffset: showCellSpinner ? "-2px" : undefined,
          boxShadow: isBusyTarget ? "inset 0 0 0 999px rgba(0, 232, 117, 0.08)" : undefined,
          transition: "box-shadow 0.2s ease, outline 0.2s ease",
        }}
      >
        {showCellSpinner && (
          <Box
            sx={{
              position: "absolute",
              top: 2,
              right: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={12} thickness={6} color="secondary" />
          </Box>
        )}
        {hasOvertime && (
          <Tooltip title={overtimeTooltipTitle} arrow placement="bottom">
            <Box
              sx={{
                position: "absolute",
                bottom: 1,
                left: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                lineHeight: 0,
              }}
            >
              <MoreTimeIcon sx={{ fontSize: 12, color: theme.palette.warning.dark }} />
            </Box>
          </Tooltip>
        )}
        <Tooltip
          title={tooltipTitle}
          arrow
          open={isEmploymentLocked ? lockedTooltipOpen : undefined}
          onOpen={isEmploymentLocked ? () => setLockedTooltipOpen(true) : undefined}
          onClose={isEmploymentLocked ? () => setLockedTooltipOpen(false) : undefined}
          disableHoverListener={!isEmploymentLocked && !hasAutoFreeDayRule && !holidayTooltip && !hasManualModification && !hasBreak && !hasLicenseInfo}
        >
          <Box
            component="span"
            sx={{
              width: "100%",
              display: "inline-flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                flexDirection: "column",
                alignItems: "center",
                lineHeight: 1,
              }}
            >
              <span
                style={{
                  color: textColor,
                  fontWeight,
                  fontStyle: isSA && !showOtherRoleShift ? "italic" : "normal",
                  display: "inline-flex",
                  alignItems: "flex-start",
                  gap: "4px",
                }}
              >
                <span>{displayText}</span>
                {hasAutoFreeDayRule && (
                  <sup style={{ fontSize: "0.65rem", lineHeight: 1, fontWeight: 700 }}>*</sup>
                )}
              </span>
              {hasManualModification && (
                <Box
                  component="span"
                  sx={{
                    width: 5,
                    height: 5,
                    mt: "3px",
                    borderRadius: "50%",
                    backgroundColor: theme.palette.primary.main,
                    opacity: 0.9,
                  }}
                />
              )}
            </Box>
          </Box>
        </Tooltip>
      </TableCell>

      {!readOnly && !isEmploymentLocked && (
        <CellMenu
          day={day}
          employeeData={{
            ...employeeData,
            workshift_id: wsId,
          }}
          station_id={station_id}
          workarea_id={workarea_id}
          workshifts={workshifts}
          anchorEl={anchorEl}
          onClose={handleMenuClose}
          onBusyChange={onBusyChange}
          busyState={busyState}
        />
      )}
    </>
  );
};

export default CalendarCell;
